<template>
  <div class="modal-card weserve-order-modal">
    <header class="modal-card-head">
      <p class="modal-card-title">
        <b-icon icon="motorcycle" size="is-small"></b-icon>
        สร้างออเดอร์ WeServe
      </p>
      <button type="button" class="delete" @click="$emit('close')"></button>
    </header>

    <section class="modal-card-body">
      <!-- Order Information -->
      <div class="order-info-form">
        <div class="columns is-mobile is-variable is-2 mb-2">
          <div class="column is-5">
            <b-field label="ออเดอร์">
              <b-input :value="orderData.id" disabled></b-input>
            </b-field>
          </div>
          <div class="column is-7">
            <b-field label="ชื่อลูกค้า">
              <b-input v-model="editableOrder.name" placeholder="ระบุชื่อลูกค้า" :disabled="currentStep === 3"></b-input>
            </b-field>
          </div>
        </div>

        <!-- Origin -->
        <div class="address-section address-section--origin">
          <div class="address-section-header">
            <span class="address-section-icon address-section-icon--origin">A</span>
            <span class="address-section-title">ต้นทาง (Origin)</span>
            <b-button v-if="currentStep !== 3" size="is-small" type="is-link" outlined icon-left="map-marker" @click="onEditOriginMap">เลือกจากแผนที่</b-button>
          </div>
          <b-field label="ที่อยู่">
            <b-input v-model="editableOrder.originAddress" type="textarea" rows="2" placeholder="ที่อยู่ต้นทาง" :disabled="currentStep === 3"></b-input>
          </b-field>
          <b-field label="เบอร์โทร">
            <b-input v-model="editableOrder.phoneVilla" placeholder="เบอร์โทร" :disabled="currentStep === 3"></b-input>
          </b-field>
          <p v-if="editableOrder.originLocation.lat" class="coord-text">
            พิกัด: {{ editableOrder.originLocation.lat.toFixed(6) }}, {{ editableOrder.originLocation.lng.toFixed(6) }}
          </p>
          <p v-else class="coord-text coord-text--missing">ยังไม่มีพิกัด - กรุณาเลือกจากแผนที่</p>
        </div>

        <!-- Destination -->
        <div class="address-section address-section--dest">
          <div class="address-section-header">
            <span class="address-section-icon address-section-icon--dest">B</span>
            <span class="address-section-title">ปลายทาง (Destination)</span>
            <b-button v-if="currentStep !== 3" size="is-small" type="is-link" outlined icon-left="map-marker" @click="onEditDestinationMap">เลือกจากแผนที่</b-button>
          </div>
          <b-field label="ที่อยู่">
            <b-input v-model="editableOrder.destinationAddress" type="textarea" rows="2" placeholder="ที่อยู่ปลายทาง" :disabled="currentStep === 3"></b-input>
          </b-field>
          <b-field label="เบอร์โทร">
            <b-input v-model="editableOrder.phone" placeholder="เบอร์โทร" :disabled="currentStep === 3"></b-input>
          </b-field>
          <p v-if="editableOrder.destinationLocation.lat" class="coord-text">
            พิกัด: {{ editableOrder.destinationLocation.lat.toFixed(6) }}, {{ editableOrder.destinationLocation.lng.toFixed(6) }}
          </p>
          <p v-else class="coord-text coord-text--missing">ยังไม่มีพิกัด - กรุณาเลือกจากแผนที่</p>
        </div>
      </div>

      <!-- Step 1: Vehicle Selection -->
      <div v-if="currentStep === 1" class="step-content">
        <h5 class="title is-5">
          <b-icon icon="truck" size="is-small"></b-icon>
          ขั้นตอนที่ 1: เลือกประเภทรถ
        </h5>
        <WeServeVehicleSelector
          v-model="selectedVehicle"
          :location="editableOrder.originLocation"
          @vehicle-selected="onVehicleSelected"
        />
      </div>

      <!-- Step 2: Addon Selection -->
      <div v-if="currentStep === 2" class="step-content">
        <h5 class="title is-5">
          <b-icon icon="plus-circle" size="is-small"></b-icon>
          ขั้นตอนที่ 2: เลือกบริการเสริม (ไม่บังคับ)
        </h5>
        <WeServeAddonSelector
          v-model="selectedAddons"
          :location="editableOrder.originLocation"
          :vehicleId="selectedVehicle ? selectedVehicle.vehicle_id : null"
          @addons-selected="onAddonsSelected"
        />
      </div>

      <!-- Step 3: Quotation Review -->
      <div v-if="currentStep === 3" class="step-content">
        <h5 class="title is-5">
          <b-icon icon="calculator" size="is-small"></b-icon>
          ขั้นตอนที่ 3: ตรวจสอบราคา
        </h5>

        <div v-if="isLoadingQuotation" class="has-text-centered">
          <b-loading :is-full-page="false" v-model="isLoadingQuotation"></b-loading>
          <p>กำลังคำนวณราคา...</p>
        </div>

        <div v-else-if="quotation" class="quotation-details">
          <!-- Selected Vehicle -->
          <div class="box">
            <h6 class="title is-6">รถที่เลือก</h6>
            <div class="media">
              <div class="media-left">
                <figure class="image is-64x64">
                  <img :src="selectedVehicle.vehicle_icon" :alt="selectedVehicle.vehicle_title_en">
                </figure>
              </div>
              <div class="media-content">
                <p class="title is-6">{{ selectedVehicle.vehicle_title_en }}</p>
                <p class="subtitle is-7">{{ selectedVehicle.vehicle_title_th }}</p>
              </div>
            </div>
          </div>

          <!-- Selected Addons -->
          <div v-if="selectedAddons.length > 0" class="box">
            <h6 class="title is-6">บริการเสริมที่เลือก</h6>
            <div class="tags">
              <span
                v-for="addon in selectedAddons"
                :key="addon.addon_id"
                class="tag is-info"
              >
                {{ addon.addon_name_en }} - {{ formatPrice(addon.addon_price) }} ฿
              </span>
            </div>
          </div>

          <!-- Price Breakdown -->
          <div class="box pricing-box">
            <h6 class="title is-6">รายละเอียดราคา</h6>
            <table class="table is-fullwidth">
              <tbody>
                <tr>
                  <td>ค่าบริการขนส่ง</td>
                  <td class="has-text-right">{{ formatPrice(quotation.transferPrice) }} ฿</td>
                </tr>
                <tr v-if="quotation.extraPointPrice > 0">
                  <td>จุดเพิ่มเติม</td>
                  <td class="has-text-right">{{ formatPrice(quotation.extraPointPrice) }} ฿</td>
                </tr>
                <tr v-if="quotation.addOnPrice > 0">
                  <td>บริการเสริม</td>
                  <td class="has-text-right">{{ formatPrice(quotation.addOnPrice) }} ฿</td>
                </tr>
                <tr>
                  <td><strong>ระยะทาง</strong></td>
                  <td class="has-text-right"><strong>{{ quotation.distance }} กม.</strong></td>
                </tr>
                <tr class="total-row">
                  <td><strong>ราคารวม</strong></td>
                  <td class="has-text-right has-text-primary">
                    <strong class="title is-4">{{ formatPrice(quotation.price) }} ฿</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Delivery Route -->
          <div class="box">
            <h6 class="title is-6">เส้นทางการจัดส่ง</h6>
            <div class="route-info">
              <div class="route-point">
                <b-icon icon="play-circle" type="is-success"></b-icon>
                <div>
                  <p class="has-text-weight-bold">รับสินค้า</p>
                  <p>{{ editableOrder.originAddress }}</p>
                </div>
              </div>
              <div class="route-divider">
                <b-icon icon="arrow-down"></b-icon>
              </div>
              <div class="route-point">
                <b-icon icon="stop-circle" type="is-danger"></b-icon>
                <div>
                  <p class="has-text-weight-bold">ส่งสินค้า</p>
                  <p>{{ editableOrder.destinationAddress }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="quotationError" class="notification is-danger">
          <p>{{ quotationError }}</p>
        </div>
      </div>
    </section>

    <footer class="modal-card-foot">
      <div class="footer-buttons">
        <div class="left-buttons">
          <b-button @click="$emit('close')">ยกเลิก</b-button>
          <b-button
            v-if="currentStep > 1"
            @click="previousStep"
            icon-pack="fas"
            icon-left="arrow-left"
          >
            ย้อนกลับ
          </b-button>
        </div>
        <div class="right-buttons">
          <b-button
            v-if="currentStep < 3"
            type="is-primary"
            @click="nextStep"
            icon-pack="fas"
            icon-right="arrow-right"
            :disabled="!canProceedToNextStep"
          >
            {{ currentStep === 2 ? 'ขอใบเสนอราคา' : 'ถัดไป' }}
          </b-button>
          <b-button
            v-if="currentStep === 3 && quotation"
            type="is-success"
            @click="placeOrder"
            icon-pack="fas"
            icon-left="check"
            :loading="isPlacingOrder"
          >
            ยืนยันและสร้างออเดอร์
          </b-button>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import WeServeVehicleSelector from "@/components/WeServeVehicleSelector";
import WeServeAddonSelector from "@/components/WeServeAddonSelector";
import EditMap from "@/components/ModalMap.vue";

export default {
  name: "WeServeOrderCreationModal",
  components: {
    WeServeVehicleSelector,
    WeServeAddonSelector
  },
  props: {
    orderData: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      currentStep: 1,
      selectedVehicle: null,
      selectedAddons: [],
      selectedAddonsFormatted: [],
      quotation: null,
      quotationError: null,
      isLoadingQuotation: false,
      isPlacingOrder: false,
      editableOrder: {
        name: "",
        originAddress: "",
        originLocation: { lat: null, lng: null },
        phoneVilla: "",
        destinationAddress: "",
        destinationLocation: { lat: null, lng: null },
        phone: ""
      }
    };
  },
  created() {
    this.editableOrder.name = this.orderData.name || "";
    this.editableOrder.originAddress = this.orderData.originAddress || "";
    this.editableOrder.originLocation = this.orderData.originLocation
      ? { ...this.orderData.originLocation }
      : { lat: null, lng: null };
    this.editableOrder.phoneVilla = this.orderData.phoneVilla || "";
    this.editableOrder.destinationAddress = this.orderData.destinationAddress || "";
    this.editableOrder.destinationLocation = this.orderData.destinationLocation
      ? { ...this.orderData.destinationLocation }
      : { lat: null, lng: null };
    this.editableOrder.phone = this.orderData.phone || "";
  },
  computed: {
    canProceedToNextStep() {
      if (this.currentStep === 1) {
        return this.selectedVehicle !== null;
      }
      return true; // Step 2 (addons) is optional
    }
  },
  methods: {
    onEditOriginMap() {
      this.$buefy.modal.open({
        parent: this,
        props: {
          initLocation: this.editableOrder.originAddress,
          initLocationLatLng: this.editableOrder.originLocation.lat
            ? this.editableOrder.originLocation
            : undefined
        },
        component: EditMap,
        hasModalCard: true,
        trapFocus: true,
        events: {
          onConfirm: (val) => {
            this.editableOrder.originAddress = val.text;
            this.editableOrder.originLocation = val.latlng;
          }
        }
      });
    },
    onEditDestinationMap() {
      this.$buefy.modal.open({
        parent: this,
        props: {
          initLocation: this.editableOrder.destinationAddress,
          initLocationLatLng: this.editableOrder.destinationLocation.lat
            ? this.editableOrder.destinationLocation
            : undefined
        },
        component: EditMap,
        hasModalCard: true,
        trapFocus: true,
        events: {
          onConfirm: (val) => {
            this.editableOrder.destinationAddress = val.text;
            this.editableOrder.destinationLocation = val.latlng;
          }
        }
      });
    },
    onVehicleSelected(vehicle) {
      this.selectedVehicle = vehicle;
      console.log("Vehicle selected:", vehicle);
    },
    onAddonsSelected(data) {
      this.selectedAddons = data.addons;
      this.selectedAddonsFormatted = data.formatted;
      console.log("Addons selected:", data);
    },
    async nextStep() {
      if (this.currentStep === 1 && !this.selectedVehicle) {
        this.$buefy.toast.open({
          message: "Please select a vehicle type",
          type: "is-warning",
          duration: 3000
        });
        return;
      }

      if (this.currentStep === 2) {
        // Get quotation before moving to step 3
        await this.getQuotation();
      } else {
        this.currentStep++;
      }
    },
    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        this.quotationError = null;
      }
    },
    async getQuotation() {
      this.isLoadingQuotation = true;
      this.quotationError = null;
      this.currentStep = 3;

      try {
        // Prepare order data with edited fields and selections
        const orderForQuotation = {
          ...this.orderData,
          name: this.editableOrder.name,
          originAddress: this.editableOrder.originAddress,
          originLocation: this.editableOrder.originLocation,
          phoneVilla: this.editableOrder.phoneVilla,
          destinationAddress: this.editableOrder.destinationAddress,
          destinationLocation: this.editableOrder.destinationLocation,
          phone: this.editableOrder.phone,
          vehicleId: this.selectedVehicle.vehicle_id,
          addons: this.selectedAddonsFormatted
        };

        const response = await this.$store.dispatch("weserve/getQuotation", orderForQuotation);

        if (response.statusCode === 200) {
          this.quotation = response;
        } else {
          // Check for specific errors
          const errorMsg = response.error || response.details?.data || "";

          if (errorMsg.includes("Latitude and Longitude are required")) {
            // Show user-friendly error and navigate to edit order
            this.$buefy.dialog.alert({
              title: "ตำแหน่งไม่ถูกต้อง",
              message: "ไม่สามารถสร้างออเดอร์ WeServe ได้ เนื่องจากที่อยู่ของออเดอร์นี้ไม่มีพิกัดละติจูดและลองจิจูด กรุณาแก้ไขที่อยู่ในออเดอร์ให้ถูกต้อง",
              confirmText: "แก้ไขออเดอร์",
              type: "is-warning",
              hasIcon: true,
              icon: "map-marker-alert",
              iconPack: "mdi",
              onConfirm: () => {
                this.$emit("close");
                this.$emit("edit-order", this.orderData);
              }
            });
            return;
          } else {
            this.quotationError = `ไม่สามารถคำนวณราคาได้: ${errorMsg}`;
          }
        }
      } catch (error) {
        console.error("Error getting quotation:", error);
        const errorMsg = error.response?.data?.error || error.message || "Unknown error";

        if (errorMsg.includes("Latitude and Longitude are required")) {
          // Show user-friendly error and navigate to edit order
          this.$buefy.dialog.alert({
            title: "ตำแหน่งไม่ถูกต้อง",
            message: "ไม่สามารถสร้างออเดอร์ WeServe ได้ เนื่องจากที่อยู่ของออเดอร์นี้ไม่มีพิกัดละติจูดและลองจิจูด กรุณาแก้ไขที่อยู่ในออเดอร์ให้ถูกต้อง",
            confirmText: "แก้ไขออเดอร์",
            type: "is-warning",
            hasIcon: true,
            icon: "map-marker-alert",
            iconPack: "mdi",
            onConfirm: () => {
              this.$emit("close");
              this.$emit("edit-order", this.orderData);
            }
          });
        } else {
          this.quotationError = `ไม่สามารถคำนวณราคาได้: ${errorMsg}`;
        }
      } finally {
        this.isLoadingQuotation = false;
      }
    },
    async placeOrder() {
      this.isPlacingOrder = true;

      try {
        // Prepare full order with edited fields and quotation data
        const orderToPlace = {
          ...this.orderData,
          name: this.editableOrder.name,
          originAddress: this.editableOrder.originAddress,
          originLocation: this.editableOrder.originLocation,
          phoneVilla: this.editableOrder.phoneVilla,
          destinationAddress: this.editableOrder.destinationAddress,
          destinationLocation: this.editableOrder.destinationLocation,
          phone: this.editableOrder.phone,
          vehicleId: this.selectedVehicle.vehicle_id,
          addons: this.selectedAddonsFormatted
        };

        const response = await this.$store.dispatch("weserve/placeOrder", orderToPlace);

        if (response.success) {
          this.$buefy.notification.open({
            message: `WeServe order created successfully! Order Code: ${response.data.weserveOrderCode}`,
            type: "is-success",
            duration: 5000,
            queue: false
          });

          this.$emit("order-placed", response.data);
          this.$emit("close");
        } else {
          throw new Error("Failed to place order");
        }
      } catch (error) {
        console.error("Error placing order:", error);
        this.$buefy.notification.open({
          message: `Failed to place order: ${error.message}`,
          type: "is-danger",
          duration: 5000,
          queue: false
        });
      } finally {
        this.isPlacingOrder = false;
      }
    },
    formatPrice(price) {
      return parseFloat(price || 0).toFixed(2);
    }
  }
};
</script>

<style scoped>
.weserve-order-modal {
  width: 800px;
  max-width: 95vw;
}

.order-info-form {
  margin-bottom: 1.25rem;
}

.address-section {
  border-left: 3px solid #dbdbdb;
  padding: 0.75rem 0 0.75rem 1rem;
  margin-bottom: 1rem;
}

.address-section--origin {
  border-left-color: #48c774;
}

.address-section--dest {
  border-left-color: #f14668;
}

.address-section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.address-section-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: #fff;
  font-weight: 700;
  font-size: 0.8rem;
  flex-shrink: 0;
}

.address-section-icon--origin {
  background: #48c774;
}

.address-section-icon--dest {
  background: #f14668;
}

.address-section-title {
  font-weight: 600;
  flex: 1;
}

.coord-text {
  font-size: 0.8rem;
  color: #999;
  margin-top: -0.25rem;
}

.coord-text--missing {
  color: #f14668;
}

.step-content {
  min-height: 300px;
}

.quotation-details {
}

.pricing-box {
  background-color: #f5f7fa;
}

.total-row {
  border-top: 2px solid #3273dc;
  font-size: 1.2rem;
}

.route-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.route-point {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.route-divider {
  text-align: center;
  margin: 0.5rem 0;
}

.footer-buttons {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.left-buttons,
.right-buttons {
  display: flex;
  gap: 0.5rem;
}

@media screen and (max-width: 768px) {
  .weserve-order-modal {
    width: 95vw;
  }

  .footer-buttons {
    flex-direction: column;
    gap: 0.5rem;
  }

  .left-buttons,
  .right-buttons {
    width: 100%;
  }
}
</style>